import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Stack, Typography, Grid, Chip, Alert, Box, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress, Snackbar, Checkbox, Divider } from '@mui/material';
import api from '../Services/Api';
import { useNavigate, useLocation } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import Loading from '../components/Loading';
import SearchComponent from '../components/SearchComponent';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import { glassCard, ACCENT, ACCENT_DARK, ACCENT_GLOW } from '../designsystem/tokens';
import { getContactName } from './contacts';
import MailOutlined from '@mui/icons-material/MailOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkOutlineOutlined from '@mui/icons-material/WorkOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import InboxIcon from '@mui/icons-material/Inbox';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SearchIcon from '@mui/icons-material/Search';
import SortOutlined from '@mui/icons-material/SortOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FilterListIcon from '@mui/icons-material/FilterList';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Task from '../record/components/Task';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);

const getPriorityColor = (priority) => {
    switch (priority) {
        case 'HIGH': return '#E57373';
        case 'MEDIUM': return '#FFB74D';
        case 'LOW': return ACCENT;
        default: return 'rgba(255,255,255,0.15)';
    }
};

const TYPE_CONFIG = {
    CALL: { Icon: PhoneOutlined, color: '#66BB6A' },
    EMAIL: { Icon: MailOutlined, color: '#42A5F5' },
};
const DEFAULT_TYPE = { Icon: CheckCircleIcon, color: '#AB47BC' };
const getTypeConfig = (type) => TYPE_CONFIG[type] || DEFAULT_TYPE;

const getStatusColor = (status) => {
    switch (status) {
        case 'IN_PROGRESS': return '#42A5F5';
        case 'NOT_STARTED': return 'rgba(255,255,255,0.4)';
        default: return 'rgba(255,255,255,0.3)';
    }
};

const NAV_BTN = {
    width: 36, height: 36, minWidth: 36,
    color: 'rgba(255,255,255,0.5)',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
};

const Tasks = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const [loading, setLoading] = useState(true);
    const crmUpdated = state?.ok ?? false;
    const [filteredTasks, setFilteredTasks] = useState([]);
    const [createdate, setCreatedate] = useState("DESCENDING");
    const previousController = useRef();
    const [dateRange, setDateRange] = useState('ALL');
    const [typeFilter, setTypeFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [selectedTasks, setSelectedTasks] = useState(new Set());
    const [rescheduleOpen, setRescheduleOpen] = useState(false);
    const [rescheduleDate, setRescheduleDate] = useState(null);
    const [batchLoading, setBatchLoading] = useState(false);
    const [batchSuccess, setBatchSuccess] = useState(false);
    const [viewMode, setViewMode] = useState('LIST');
    const [calendarSubMode, setCalendarSubMode] = useState('WEEK');
    const [searchOpen, setSearchOpen] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [weekOffset, setWeekOffset] = useState(0);
    const [dayOffset, setDayOffset] = useState(0);
    const [monthOffset, setMonthOffset] = useState(0);
    const [selectedDay, setSelectedDay] = useState(() => dayjs());
    const navigate = useNavigate();

    const taskTypes = useMemo(() => {
        const types = new Set();
        filteredTasks.forEach(task => { if (task.type) types.add(task.type); });
        return Array.from(types);
    }, [filteredTasks]);

    // Calendar views use this (no dateRange filter)
    const calendarTasks = useMemo(() => {
        let tasks = filteredTasks.filter(task => task.status !== 'COMPLETED');
        if (typeFilter) tasks = tasks.filter(task => task.type === typeFilter);
        if (priorityFilter) tasks = tasks.filter(task => task.priority === priorityFilter);
        return tasks;
    }, [filteredTasks, typeFilter, priorityFilter]);

    // LIST view uses this (includes dateRange filter)
    const displayTasks = useMemo(() => {
        let tasks = filteredTasks.filter(task => task.status !== 'COMPLETED');
        if (dateRange !== 'ALL') {
            const now = dayjs();
            tasks = tasks.filter(task => {
                if (!task.dueDate) return dateRange === 'OVERDUE';
                const due = dayjs(task.dueDate.split('Z')[0]);
                switch (dateRange) {
                    case 'TODAY': return due.isSame(now, 'day');
                    case 'OVERDUE': return due.isBefore(now, 'day');
                    case 'WEEK': return due.isAfter(now.startOf('week').subtract(1, 'day')) && due.isBefore(now.endOf('week').add(1, 'day'));
                    case 'MONTH': return due.isAfter(now.startOf('month').subtract(1, 'day')) && due.isBefore(now.endOf('month').add(1, 'day'));
                    default: return true;
                }
            });
        }
        if (typeFilter) tasks = tasks.filter(task => task.type === typeFilter);
        if (priorityFilter) tasks = tasks.filter(task => task.priority === priorityFilter);
        return tasks;
    }, [filteredTasks, dateRange, typeFilter, priorityFilter]);

    const groupedTasks = useMemo(() => {
        const groups = {};
        displayTasks.forEach(task => {
            const dateKey = task.dueDate
                ? dayjs(task.dueDate.split('Z')[0]).tz(dayjs.tz.guess()).format('YYYY-MM-DD')
                : 'no-date';
            if (!groups[dateKey]) groups[dateKey] = [];
            groups[dateKey].push(task);
        });
        return Object.entries(groups).sort(([a], [b]) => {
            if (a === 'no-date') return 1;
            if (b === 'no-date') return -1;
            return createdate === 'DESCENDING' ? b.localeCompare(a) : a.localeCompare(b);
        });
    }, [displayTasks, createdate]);

    // WEEK view
    const currentWeekStart = useMemo(() => dayjs().add(weekOffset * 7, 'day'), [weekOffset]);
    const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => currentWeekStart.add(i, 'day')), [currentWeekStart]);
    const weekTasks = useMemo(() => {
        const byDay = {};
        weekDays.forEach(day => { byDay[day.format('YYYY-MM-DD')] = []; });
        calendarTasks.forEach(task => {
            if (!task.dueDate) return;
            const key = dayjs(task.dueDate.split('Z')[0]).format('YYYY-MM-DD');
            if (byDay[key]) byDay[key].push(task);
        });
        return byDay;
    }, [calendarTasks, weekDays]);

    // DAY view
    const currentDay = useMemo(() => dayjs().add(dayOffset, 'day'), [dayOffset]);
    const dayTasks = useMemo(() => {
        const key = currentDay.format('YYYY-MM-DD');
        return calendarTasks
            .filter(task => {
                if (!task.dueDate) return false;
                return dayjs(task.dueDate.split('Z')[0]).format('YYYY-MM-DD') === key;
            })
            .sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf());
    }, [currentDay, calendarTasks]);

    // MONTH view
    const monthGridData = useMemo(() => {
        const monthStart = dayjs().add(monthOffset, 'month').startOf('month');
        const gridStart = monthStart.startOf('week');
        const gridEnd = monthStart.endOf('month').endOf('week');
        const days = [];
        let d = gridStart;
        while (d.isBefore(gridEnd) || d.isSame(gridEnd, 'day')) {
            days.push(d);
            d = d.add(1, 'day');
        }
        return { monthStart, days };
    }, [monthOffset]);

    const monthTaskMap = useMemo(() => {
        const map = {};
        calendarTasks.forEach(task => {
            if (!task.dueDate) return;
            const key = dayjs(task.dueDate.split('Z')[0]).format('YYYY-MM-DD');
            if (!map[key]) map[key] = [];
            map[key].push(task);
        });
        return map;
    }, [calendarTasks]);

    const selectedDayTasks = useMemo(() => {
        if (!(viewMode === 'CALENDAR' && calendarSubMode === 'MONTH') || !selectedDay) return [];
        const key = selectedDay.format('YYYY-MM-DD');
        return (monthTaskMap[key] || []).sort((a, b) => dayjs(a.dueDate).valueOf() - dayjs(b.dueDate).valueOf());
    }, [selectedDay, monthTaskMap, viewMode, calendarSubMode]);

    const getDateLabel = useCallback((dateKey) => {
        if (dateKey === 'no-date') return t('noDate');
        const date = dayjs(dateKey);
        const today = dayjs();
        if (date.isSame(today, 'day')) return t('today');
        if (date.isSame(today.add(1, 'day'), 'day')) return t('tomorrow');
        if (date.isSame(today.subtract(1, 'day'), 'day')) return t('yesterday');
        return date.format('DD/MM/YYYY');
    }, [t]);

    useEffect(() => {
        api.fetchTasks("", createdate).then((response) => {
            setFilteredTasks(response);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching tasks", error);
            setLoading(false);
        });
    }, []);

    const handleSearchChange = (searchTerm) => {
        if (previousController.current) previousController.current.abort();
        const controller = new AbortController();
        previousController.current = controller;
        setLoading(true);
        api.fetchTasks(searchTerm.toLowerCase(), createdate, undefined, controller.signal).then((response) => {
            setFilteredTasks(response);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching tasks", error);
            setLoading(false);
        });
    };

    const handleSortChange = (searchTerm, createdate) => {
        setLoading(true);
        setCreatedate(createdate);
        api.fetchTasks(searchTerm.toLowerCase(), createdate).then((response) => {
            setFilteredTasks(response);
            setLoading(false);
        }).catch((error) => {
            console.error("Error fetching tasks", error);
            setLoading(false);
        });
    };

    const toggleTaskSelection = (taskId, e) => {
        if (e) e.stopPropagation();
        setSelectedTasks(prev => {
            const next = new Set(prev);
            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const currentTasks = (viewMode === 'CALENDAR' && calendarSubMode === 'DAY') ? dayTasks : displayTasks;
        if (selectedTasks.size === currentTasks.length && currentTasks.length > 0) {
            setSelectedTasks(new Set());
        } else {
            setSelectedTasks(new Set(currentTasks.map(t => t.id)));
        }
    };

    const refetchTasks = () => {
        setLoading(true);
        api.fetchTasks("", createdate).then((response) => {
            setFilteredTasks(response);
            setLoading(false);
        }).catch((err) => {
            console.error("Error fetching tasks", err);
            setLoading(false);
        });
    };

    const handleBatchComplete = () => {
        setBatchLoading(true);
        Promise.all(
            Array.from(selectedTasks).map(id => api.updateTask(id, { status: 'COMPLETED' }))
        ).then(() => {
            setSelectedTasks(new Set());
            setBatchLoading(false);
            setBatchSuccess(true);
            refetchTasks();
        }).catch((err) => {
            console.error("Error completing tasks", err);
            setBatchLoading(false);
        });
    };

    const handleBatchReschedule = () => {
        if (!rescheduleDate) return;
        setBatchLoading(true);
        const isoDate = rescheduleDate.valueOf();
        Promise.all(
            Array.from(selectedTasks).map(id => api.updateTask(id, { dueDate: isoDate }))
        ).then(() => {
            setSelectedTasks(new Set());
            setRescheduleOpen(false);
            setRescheduleDate(null);
            setBatchLoading(false);
            setBatchSuccess(true);
            refetchTasks();
        }).catch((err) => {
            console.error("Error rescheduling tasks", err);
            setBatchLoading(false);
        });
    };

    const showBatchSelection = viewMode === 'LIST' || (viewMode === 'CALENDAR' && calendarSubMode === 'DAY');
    const activeTasks = (viewMode === 'CALENDAR' && calendarSubMode === 'DAY') ? dayTasks : displayTasks;

    const handleCalendarPrev = () => {
        if (calendarSubMode === 'DAY') setDayOffset(d => d - 1);
        else if (calendarSubMode === 'WEEK') setWeekOffset(w => w - 1);
        else { setMonthOffset(m => m - 1); setSelectedDay(dayjs().add(monthOffset - 1, 'month').startOf('month')); }
    };
    const handleCalendarNext = () => {
        if (calendarSubMode === 'DAY') setDayOffset(d => d + 1);
        else if (calendarSubMode === 'WEEK') setWeekOffset(w => w + 1);
        else { setMonthOffset(m => m + 1); setSelectedDay(dayjs().add(monthOffset + 1, 'month').startOf('month')); }
    };
    const handleCalendarToday = () => {
        if (calendarSubMode === 'DAY') setDayOffset(0);
        else if (calendarSubMode === 'WEEK') setWeekOffset(0);
        else { setMonthOffset(0); setSelectedDay(dayjs()); }
    };
    const isCalendarAtToday = calendarSubMode === 'DAY' ? dayOffset === 0 : calendarSubMode === 'WEEK' ? weekOffset === 0 : monthOffset === 0;

    return (
        <Stack style={{ padding: '1rem', width: '100%', margin: 'auto' }}>
            {/* Header */}
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
                <IconButton aria-label={t('goBack')} data-testid="back-button" sx={{ color: 'white' }} onClick={() => navigate("/app")}>
                    <ArrowBack />
                </IconButton>
                <Typography variant="h5" sx={{ color: 'white', textAlign: 'left', flex: 1 }}>{t('tasks')}</Typography>
                <IconButton
                    aria-label={t('filters')}
                    data-testid="filter-toggle"
                    onClick={() => setFiltersOpen(o => !o)}
                    sx={{
                        color: filtersOpen || priorityFilter || typeFilter || dateRange !== 'ALL' ? ACCENT : 'rgba(255,255,255,0.5)',
                        minWidth: 44, minHeight: 44,
                    }}
                >
                    <FilterListIcon />
                </IconButton>
                <IconButton
                    aria-label={searchOpen ? t('cancel') : t('search')}
                    data-testid="search-toggle"
                    onClick={() => setSearchOpen(o => !o)}
                    sx={{ color: searchOpen ? ACCENT : 'rgba(255,255,255,0.5)', minWidth: 44, minHeight: 44 }}
                >
                    {searchOpen ? <CloseIcon /> : <SearchIcon />}
                </IconButton>
            </Stack>

            <Loading loading={loading} />

            <Snackbar open={crmUpdated} autoHideDuration={4000} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert icon={<CheckIcon style={{ color: "white" }} fontSize="inherit" />} sx={{ borderRadius: '12px', backgroundColor: '#366E6B', color: 'white' }}>
                    {t('crmUpdated')}
                </Alert>
            </Snackbar>

            {/* Collapsible search */}
            {searchOpen && (
                <SearchComponent createdate={createdate} handleSortChange={handleSortChange} handleSearchChange={handleSearchChange} titleNewest={'nearest'} titleOldest={'farthest'} />
            )}

            {/* View mode toggle: LIST vs CALENDAR */}
            <Stack direction="row" alignItems="center" sx={{ py: 1, gap: 0.5 }}>
                <Stack direction="row" spacing={0.5} sx={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '12px', p: 0.5, flexShrink: 0 }}>
                    {[
                        { value: 'LIST', label: t('list'), icon: <FormatListBulletedIcon sx={{ fontSize: 16 }} /> },
                        { value: 'CALENDAR', label: t('calendar'), icon: <CalendarMonthIcon sx={{ fontSize: 16 }} /> },
                    ].map(v => (
                        <Chip
                            key={v.value}
                            icon={v.icon}
                            label={v.label}
                            data-testid={`view-${v.value.toLowerCase()}`}
                            onClick={() => { setViewMode(v.value); setSelectedTasks(new Set()); }}
                            sx={{
                                height: 34, borderRadius: '10px', fontSize: '0.78rem', px: 0.5,
                                fontWeight: viewMode === v.value ? 600 : 400,
                                backgroundColor: viewMode === v.value ? ACCENT : 'transparent',
                                color: viewMode === v.value ? '#0a1929' : 'rgba(255,255,255,0.5)',
                                '& .MuiChip-icon': { color: viewMode === v.value ? '#0a1929' : 'rgba(255,255,255,0.4)' },
                                '&:hover': { backgroundColor: viewMode === v.value ? ACCENT : 'rgba(255,255,255,0.08)' },
                            }}
                        />
                    ))}
                </Stack>

                <Box sx={{ flex: 1 }} />

                {/* Calendar sub-mode chips + navigation */}
                {viewMode === 'CALENDAR' && (
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        {[
                            { value: 'WEEK', label: t('weekView') },
                            { value: 'DAY', label: t('dayView') },
                            { value: 'MONTH', label: t('monthView') },
                        ].map(s => (
                            <Chip
                                key={s.value}
                                label={s.label}
                                size="small"
                                data-testid={`cal-sub-${s.value.toLowerCase()}`}
                                onClick={() => { setCalendarSubMode(s.value); setSelectedTasks(new Set()); }}
                                sx={{
                                    height: 26, borderRadius: '8px', fontSize: '0.68rem',
                                    fontWeight: calendarSubMode === s.value ? 600 : 400,
                                    backgroundColor: calendarSubMode === s.value ? `${ACCENT}25` : 'transparent',
                                    color: calendarSubMode === s.value ? ACCENT : 'rgba(255,255,255,0.4)',
                                    border: calendarSubMode === s.value ? `1px solid ${ACCENT}40` : '1px solid rgba(255,255,255,0.06)',
                                    '&:hover': { backgroundColor: calendarSubMode === s.value ? `${ACCENT}25` : 'rgba(255,255,255,0.05)' },
                                }}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>

            {/* Calendar navigation row */}
            {viewMode === 'CALENDAR' && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 0.75 }}>
                    <IconButton size="small" onClick={handleCalendarPrev} sx={NAV_BTN} aria-label={t('goBack')}>
                        <NavigateBeforeIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem', fontWeight: 500, textTransform: 'capitalize', flex: 1, textAlign: 'center' }}>
                        {calendarSubMode === 'DAY' && currentDay.format('dddd, D MMMM YYYY')}
                        {calendarSubMode === 'WEEK' && `${currentWeekStart.format('D MMM')} – ${currentWeekStart.add(6, 'day').format('D MMM YYYY')}`}
                        {calendarSubMode === 'MONTH' && monthGridData.monthStart.format('MMMM YYYY')}
                    </Typography>
                    <Chip label={t('today')} size="small" onClick={handleCalendarToday}
                        sx={{ height: 26, borderRadius: '8px', fontSize: '0.68rem', fontWeight: 600, mx: 0.5,
                            backgroundColor: isCalendarAtToday ? ACCENT : 'rgba(255,255,255,0.06)',
                            color: isCalendarAtToday ? '#0a1929' : 'rgba(255,255,255,0.5)' }} />
                    <IconButton size="small" onClick={handleCalendarNext} sx={NAV_BTN} aria-label={t('tomorrow')}>
                        <NavigateNextIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                </Stack>
            )}

            {/* Collapsible filters: Priority + Type + Date range */}
            {filtersOpen && (<>
                <Box
                    className="hide-scrollbar"
                    data-testid="filter-row"
                    sx={{ display: 'flex', gap: 0.5, overflowX: 'auto', pb: 1, alignItems: 'center' }}
                >
                    {[
                        { value: null, label: t('all') },
                        { value: 'HIGH', label: t('high'), color: '#E57373' },
                        { value: 'MEDIUM', label: t('medium'), color: '#FFB74D' },
                        { value: 'LOW', label: t('low'), color: ACCENT },
                        { value: 'NONE', label: t('noPriority'), color: 'rgba(255,255,255,0.3)' },
                    ].map(p => (
                        <Chip
                            key={p.value ?? 'all-p'}
                            label={p.label}
                            icon={p.color ? <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: p.color, flexShrink: 0, ml: '8px !important', mr: '-2px !important' }} /> : undefined}
                            onClick={() => { setPriorityFilter(p.value); setSelectedTasks(new Set()); }}
                            size="small"
                            sx={{
                                height: 28, borderRadius: '14px', fontSize: '0.72rem', flexShrink: 0,
                                backgroundColor: priorityFilter === p.value ? (p.color ? `${p.color}18` : ACCENT) : 'transparent',
                                color: priorityFilter === p.value ? (p.value === null ? '#0a1929' : (p.color || 'white')) : 'rgba(255,255,255,0.45)',
                                fontWeight: priorityFilter === p.value ? 600 : 400,
                                border: priorityFilter === p.value ? (p.value === null ? 'none' : `1px solid ${p.color}40`) : '1px solid rgba(255,255,255,0.08)',
                                '&:hover': { backgroundColor: priorityFilter === p.value ? undefined : 'rgba(255,255,255,0.05)' },
                            }}
                        />
                    ))}

                    {taskTypes.length > 0 && (
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: 'rgba(255,255,255,0.1)' }} />
                    )}

                    {taskTypes.length > 0 && [
                        { value: null, label: t('all') },
                        ...taskTypes.map(type => ({ value: type, label: t(type.toLowerCase()) })),
                    ].map(tp => (
                        <Chip
                            key={`t-${tp.value ?? 'all'}`}
                            label={tp.label}
                            onClick={() => { setTypeFilter(tp.value === typeFilter ? null : tp.value); setSelectedTasks(new Set()); }}
                            size="small"
                            sx={{
                                height: 28, borderRadius: '14px', fontSize: '0.72rem', flexShrink: 0,
                                backgroundColor: typeFilter === tp.value ? ACCENT : 'transparent',
                                color: typeFilter === tp.value ? '#0a1929' : 'rgba(255,255,255,0.45)',
                                fontWeight: typeFilter === tp.value ? 600 : 400,
                                border: typeFilter === tp.value ? 'none' : '1px solid rgba(255,255,255,0.08)',
                                '&:hover': { backgroundColor: typeFilter === tp.value ? ACCENT : 'rgba(255,255,255,0.05)' },
                            }}
                        />
                    ))}
                </Box>

                {/* Date range - LIST only */}
                {viewMode === 'LIST' && (
                    <Box
                        className="hide-scrollbar"
                        role="tablist"
                        aria-label={t('dateRange')}
                        sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 1 }}
                    >
                        {[
                            { value: 'ALL', label: t('all') },
                            { value: 'TODAY', label: t('dueToday') },
                            { value: 'OVERDUE', label: t('overdue'), color: '#E57373' },
                        ].map(r => (
                            <Chip
                                key={r.value}
                                role="tab"
                                aria-selected={dateRange === r.value}
                                label={r.label}
                                onClick={() => { setDateRange(r.value); setSelectedTasks(new Set()); }}
                                sx={{
                                    height: 32, borderRadius: '16px', fontWeight: dateRange === r.value ? 600 : 400,
                                    fontSize: '0.75rem', flexShrink: 0,
                                    backgroundColor: dateRange === r.value ? (r.color ? `${r.color}20` : 'rgba(118,169,172,0.15)') : 'transparent',
                                    color: dateRange === r.value ? (r.color || ACCENT) : 'rgba(255,255,255,0.5)',
                                    border: dateRange === r.value ? `1px solid ${r.color || ACCENT}` : '1px solid rgba(255,255,255,0.08)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { backgroundColor: dateRange === r.value ? (r.color ? `${r.color}30` : 'rgba(118,169,172,0.2)') : 'rgba(255,255,255,0.05)' },
                                    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                }}
                            />
                        ))}
                    </Box>
                )}
            </>)}

            {/* Task count + sort + select all (LIST / DAY) */}
            {showBatchSelection && !loading && activeTasks.length > 0 && (
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1, px: 0.5 }}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                            {activeTasks.length} {t('tasks').toLowerCase()}
                        </Typography>
                        {viewMode === 'LIST' && (
                            <IconButton
                                size="small"
                                aria-label={createdate === 'DESCENDING' ? t('nearest') : t('farthest')}
                                data-testid="sort-toggle"
                                onClick={() => handleSortChange('', createdate === 'DESCENDING' ? 'ASCENDING' : 'DESCENDING')}
                                sx={{ color: 'rgba(255,255,255,0.35)', p: 0.5, minWidth: 36, minHeight: 36 }}
                            >
                                <SortOutlined sx={{ fontSize: 18, transform: createdate === 'ASCENDING' ? 'scaleY(-1)' : 'none' }} />
                            </IconButton>
                        )}
                        {selectedTasks.size === 0 && (
                            <Typography sx={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.68rem', fontStyle: 'italic' }}>
                                {t('tapToSelect')}
                            </Typography>
                        )}
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: 'pointer', minHeight: 44, px: 0.5 }} onClick={toggleSelectAll}>
                        <Checkbox
                            checked={selectedTasks.size === activeTasks.length && activeTasks.length > 0}
                            indeterminate={selectedTasks.size > 0 && selectedTasks.size < activeTasks.length}
                            size="small"
                            sx={{ color: 'rgba(255,255,255,0.25)', '&.Mui-checked': { color: ACCENT }, '&.MuiCheckbox-indeterminate': { color: ACCENT }, p: 0 }}
                        />
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}>
                            {selectedTasks.size > 0 ? `${selectedTasks.size} ${t('selected')}` : t('selectAll')}
                        </Typography>
                    </Stack>
                </Stack>
            )}

            {/* Empty state */}
            {showBatchSelection && !loading && activeTasks.length === 0 && (
                <Stack alignItems="center" justifyContent="center" spacing={2} sx={{ py: 8, color: 'rgba(255,255,255,0.4)' }}>
                    <InboxIcon sx={{ fontSize: 48 }} />
                    <Typography variant="body1">{(viewMode === 'CALENDAR' && calendarSubMode === 'DAY') ? t('noTasksOnDay') : t('noTasksFound')}</Typography>
                </Stack>
            )}

            {/* ─── LIST VIEW ─── */}
            {viewMode === 'LIST' && (
                <Stack data-testid="list-container" spacing={2} sx={{ mt: 1, pb: selectedTasks.size > 0 ? 10 : 0 }}>
                    {groupedTasks.map(([dateKey, tasks]) => (
                        <Stack key={dateKey} spacing={1}>
                            <Typography sx={{
                                fontSize: '0.8rem', fontWeight: 600,
                                color: dateKey === dayjs().format('YYYY-MM-DD') ? ACCENT : 'rgba(255,255,255,0.5)',
                                textTransform: 'uppercase', letterSpacing: '0.06em', px: 0.5, pt: 0.5,
                            }}>
                                {getDateLabel(dateKey)}
                            </Typography>
                            <Grid container spacing={1}>
                                {tasks.map((task, idx) => (
                                    <Grid key={task.id || idx} item xs={12} sm={6} md={4} lg={3}>
                                        <TaskCard task={task} selected={selectedTasks.has(task.id)} onToggleSelect={toggleTaskSelection} />
                                    </Grid>
                                ))}
                            </Grid>
                        </Stack>
                    ))}
                </Stack>
            )}

            {/* ─── DAY VIEW ─── */}
            {viewMode === 'CALENDAR' && calendarSubMode === 'DAY' && !loading && dayTasks.length > 0 && (
                <Stack data-testid="day-container" spacing={1} sx={{ mt: 1, pb: selectedTasks.size > 0 ? 10 : 0 }}>
                    {dayTasks.map((task, idx) => (
                        <TaskCard key={task.id || idx} task={task} selected={selectedTasks.has(task.id)} onToggleSelect={toggleTaskSelection} />
                    ))}
                </Stack>
            )}

            {/* ─── WEEK VIEW ─── */}
            {viewMode === 'CALENDAR' && calendarSubMode === 'WEEK' && !loading && (
                <Box
                    className="hide-scrollbar"
                    data-testid="week-container"
                    sx={{
                        display: 'flex', gap: 0.75, overflowX: 'auto', mt: 1, pb: 2,
                        scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch',
                    }}
                >
                    {weekDays.map(day => {
                        const key = day.format('YYYY-MM-DD');
                        const isToday = day.isSame(dayjs(), 'day');
                        const tasks = weekTasks[key] || [];
                        return (
                            <Stack key={key} sx={{
                                minWidth: { xs: 'calc((100vw - 2rem - 4.5px) / 3)', sm: 140 },
                                flex: '1 0 auto',
                                scrollSnapAlign: 'start',
                            }}>
                                <Stack alignItems="center" sx={{
                                    py: 1, mb: 0.75, borderRadius: '10px',
                                    backgroundColor: isToday ? `${ACCENT}12` : 'transparent',
                                    border: isToday ? `1px solid ${ACCENT}30` : '1px solid transparent',
                                }}>
                                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 500, color: isToday ? ACCENT : 'rgba(255,255,255,0.4)', textTransform: 'uppercase' }}>
                                        {day.format('ddd')}
                                    </Typography>
                                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: isToday ? ACCENT : 'white' }}>
                                        {day.format('D')}
                                    </Typography>
                                </Stack>
                                <Stack spacing={0.75}>
                                    {tasks.map(task => (
                                        <WeekTaskCard key={task.id} task={task} />
                                    ))}
                                    {tasks.length === 0 && (
                                        <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.12)', textAlign: 'center', py: 2 }}>{'\u2014'}</Typography>
                                    )}
                                </Stack>
                            </Stack>
                        );
                    })}
                </Box>
            )}

            {/* ─── MONTH VIEW ─── */}
            {viewMode === 'CALENDAR' && calendarSubMode === 'MONTH' && !loading && (
                <Stack data-testid="month-container" spacing={1.5} sx={{ mt: 1 }}>
                    {/* Day-of-week headers */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                        {Array.from({ length: 7 }, (_, i) => dayjs().startOf('week').add(i, 'day')).map(d => (
                            <Typography key={d.format('dd')} sx={{
                                textAlign: 'center', fontSize: '0.68rem', fontWeight: 600,
                                color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', py: 0.5,
                            }}>
                                {d.format('dd')}
                            </Typography>
                        ))}
                    </Box>

                    {/* Calendar grid */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                        {monthGridData.days.map(day => {
                            const key = day.format('YYYY-MM-DD');
                            const isToday = day.isSame(dayjs(), 'day');
                            const isCurrentMonth = day.month() === monthGridData.monthStart.month();
                            const isSelected = selectedDay?.isSame(day, 'day');
                            const tasksForDay = monthTaskMap[key] || [];
                            const dots = tasksForDay.slice(0, 3);
                            const overflow = tasksForDay.length > 3;

                            return (
                                <Box
                                    key={key}
                                    onClick={() => setSelectedDay(day)}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={`${day.format('D MMMM')}, ${tasksForDay.length} ${t('tasks').toLowerCase()}`}
                                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedDay(day); } }}
                                    sx={{
                                        minHeight: 48, p: 0.5, borderRadius: '10px', cursor: 'pointer',
                                        opacity: isCurrentMonth ? 1 : 0.2,
                                        backgroundColor: isSelected ? `${ACCENT_DARK}30` : 'transparent',
                                        border: isToday ? `1.5px solid ${ACCENT}` : '1.5px solid transparent',
                                        transition: 'background-color 0.15s ease',
                                        '&:hover': { backgroundColor: isSelected ? `${ACCENT_DARK}30` : 'rgba(255,255,255,0.04)' },
                                        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 1 },
                                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: 0.25,
                                    }}
                                >
                                    <Typography sx={{
                                        fontSize: '0.78rem', fontWeight: isToday ? 700 : 500,
                                        color: isToday ? ACCENT : (isSelected ? 'white' : 'rgba(255,255,255,0.7)'),
                                        lineHeight: 1.4,
                                    }}>
                                        {day.format('D')}
                                    </Typography>
                                    {tasksForDay.length > 0 && (
                                        <Stack direction="row" spacing={0.25} alignItems="center">
                                            {dots.map((task, i) => (
                                                <Box key={i} sx={{
                                                    width: 5, height: 5, borderRadius: '50%',
                                                    backgroundColor: getPriorityColor(task.priority),
                                                }} />
                                            ))}
                                            {overflow && (
                                                <Typography sx={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600, lineHeight: 1 }}>
                                                    +{tasksForDay.length - 3}
                                                </Typography>
                                            )}
                                        </Stack>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>

                    {/* Selected day tasks */}
                    {selectedDay && (
                        <Stack spacing={1}>
                            <Typography sx={{
                                fontSize: '0.8rem', fontWeight: 600, color: ACCENT,
                                textTransform: 'uppercase', letterSpacing: '0.06em', px: 0.5,
                            }}>
                                {selectedDay.isSame(dayjs(), 'day') ? t('today') : selectedDay.format('dddd, D MMM')}
                            </Typography>
                            {selectedDayTasks.length === 0 ? (
                                <Stack alignItems="center" spacing={1} sx={{ py: 3, color: 'rgba(255,255,255,0.3)' }}>
                                    <CalendarMonthIcon sx={{ fontSize: 32 }} />
                                    <Typography sx={{ fontSize: '0.82rem' }}>{t('noTasksOnDay')}</Typography>
                                </Stack>
                            ) : (
                                selectedDayTasks.map(task => (
                                    <WeekTaskCard key={task.id} task={task} />
                                ))
                            )}
                        </Stack>
                    )}
                </Stack>
            )}

            {/* Floating batch action bar */}
            {selectedTasks.size > 0 && (
                <Box sx={{
                    position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)', zIndex: 1200,
                    display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25,
                    borderRadius: '16px', backgroundColor: 'rgba(15,27,46,0.95)',
                    border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(16px)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)', maxWidth: 'calc(100vw - 32px)',
                }}>
                    {batchLoading ? (
                        <CircularProgress size={20} sx={{ color: ACCENT }} />
                    ) : (<>
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', mr: 0.5 }}>
                            {selectedTasks.size}
                        </Typography>
                        <Button size="small" startIcon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={handleBatchComplete}
                            data-testid="batch-complete"
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
                                color: '#0a1929', backgroundColor: '#66BB6A', px: 1.5, minWidth: 0, whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: '#81C784' },
                            }}
                        >
                            {t('complete')}
                        </Button>
                        <Button size="small" startIcon={<EventRepeatIcon sx={{ fontSize: '16px !important' }} />}
                            onClick={() => setRescheduleOpen(true)}
                            data-testid="batch-reschedule"
                            sx={{
                                borderRadius: '12px', textTransform: 'none', fontSize: '0.78rem', fontWeight: 600,
                                color: 'white', backgroundColor: '#42A5F5', px: 1.5, minWidth: 0, whiteSpace: 'nowrap',
                                '&:hover': { backgroundColor: '#64B5F6' },
                            }}
                        >
                            {t('reschedule')}
                        </Button>
                        <IconButton size="small" onClick={() => setSelectedTasks(new Set())}
                            aria-label={t('cancel')}
                            sx={{ color: 'rgba(255,255,255,0.4)', p: 0.75, minWidth: 36, minHeight: 36 }}
                        >
                            <CloseIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </>)}
                </Box>
            )}

            {/* Reschedule dialog */}
            <Dialog open={rescheduleOpen} onClose={() => setRescheduleOpen(false)} aria-labelledby="reschedule-title"
                PaperProps={{ sx: { backgroundColor: '#0f1b2e', backgroundImage: 'none', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', maxWidth: 400, width: '100%', mx: 2 } }}
            >
                <DialogTitle id="reschedule-title" sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', pb: 0.5 }}>
                    {t('reschedule')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2, color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                        {t('rescheduleDescription')}
                    </DialogContentText>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileDateTimePicker
                            label={t('newDate')}
                            value={rescheduleDate}
                            onChange={setRescheduleDate}
                            data-testid="reschedule-picker"
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => { setRescheduleOpen(false); setRescheduleDate(null); }}
                        sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, borderRadius: '12px', px: 2.5 }}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleBatchReschedule} disabled={!rescheduleDate || batchLoading}
                        sx={{
                            color: '#0a1929', fontWeight: 600, backgroundColor: '#42A5F5', borderRadius: '12px', px: 2.5,
                            '&:hover': { backgroundColor: '#64B5F6' },
                            '&:disabled': { backgroundColor: 'rgba(66,165,245,0.3)', color: 'rgba(255,255,255,0.3)' },
                        }}>
                        {batchLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('reschedule')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={batchSuccess} autoHideDuration={3000} onClose={() => setBatchSuccess(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success" onClose={() => setBatchSuccess(false)} sx={{ borderRadius: '12px' }}>
                    {t('tasksUpdated')}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

const TaskCard = ({ task, selected = false, onToggleSelect }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const handleClick = () => navigate("/app/tasks/detailed", { state: { task } });
    const handleKeyDown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } };
    const isOverdue = task.dueDate && dayjs(task.dueDate.split('Z')[0]).isBefore(dayjs(), 'day');

    const priorityColor = getPriorityColor(task.priority);
    const { Icon: TypeIcon, color: typeColor } = getTypeConfig(task.type);

    return (
        <Box
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-label={task.title ?? t('task')}
            data-testid="task-card"
            sx={{
                ...glassCard,
                cursor: 'pointer',
                border: `1.5px solid ${priorityColor}50`,
                transition: 'transform 0.15s ease, box-shadow 0.2s ease',
                ...(selected && { backgroundColor: 'rgba(118,169,172,0.08)', border: `1.5px solid ${ACCENT}` }),
                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${ACCENT_GLOW}` },
                '&:active': { transform: 'scale(0.98)' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' }, '&:active': { transform: 'none' } },
            }}
        >
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Box
                    onClick={(e) => { e.stopPropagation(); onToggleSelect(task.id, e); }}
                    aria-label={selected ? t('deselect') : t('tapToSelect')}
                    sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        backgroundColor: selected ? `${ACCENT}20` : `${typeColor}1F`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0, cursor: 'pointer', position: 'relative',
                        transition: 'background-color 0.15s ease',
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                >
                    {selected
                        ? <CheckIcon sx={{ fontSize: 20, color: ACCENT }} />
                        : <TypeIcon sx={{ fontSize: 18, color: typeColor }} />
                    }
                    {/* Selection indicator */}
                    <Box sx={{
                        position: 'absolute', bottom: -1, right: -1,
                        width: 14, height: 14, borderRadius: '50%',
                        backgroundColor: selected ? ACCENT : 'rgba(255,255,255,0.12)',
                        border: '2px solid #0a1929',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        {selected && <CheckIcon sx={{ fontSize: 9, color: '#0a1929' }} />}
                    </Box>
                </Box>
                <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: 'white', lineHeight: 1.3 }}>
                        {task.title ?? '--'}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                        {task.dueDate && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                                <CalendarMonthIcon sx={{ fontSize: 13, color: isOverdue ? '#E57373' : 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, color: isOverdue ? '#E57373' : 'rgba(255,255,255,0.5)' }}>
                                    {dayjs(task.dueDate.split('Z')[0]).tz(dayjs.tz.guess()).format('DD/MM HH:mm')}
                                </Typography>
                            </Stack>
                        )}
                        {isOverdue && (
                            <Chip label={t('overdue')} size="small"
                                sx={{
                                    height: 18, fontSize: '0.62rem', fontWeight: 700, borderRadius: '6px',
                                    backgroundColor: 'rgba(229,115,115,0.15)', color: '#E57373', border: 'none',
                                    textTransform: 'uppercase', letterSpacing: '0.04em',
                                }}
                            />
                        )}
                        {task.priority && task.priority !== 'NONE' && (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: priorityColor, flexShrink: 0 }} />
                        )}
                    </Stack>
                </Stack>
            </Stack>
        </Box>
    );
};

const WeekTaskCard = ({ task }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { Icon: TypeIcon, color: typeColor } = getTypeConfig(task.type);
    const priorityColor = getPriorityColor(task.priority);
    const isOverdue = task.dueDate && dayjs(task.dueDate.split('Z')[0]).isBefore(dayjs(), 'day');
    const time = task.dueDate ? dayjs(task.dueDate.split('Z')[0]).tz(dayjs.tz.guess()).format('HH:mm') : '';
    const handleClick = () => navigate("/app/tasks/detailed", { state: { task } });
    const handleKeyDown = (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } };

    return (
        <Box
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="button"
            aria-label={`${task.title ?? t('task')}${time ? `, ${time}` : ''}`}
            data-testid="week-task-card"
            sx={{
                ...glassCard,
                p: 1.25, minHeight: 44,
                cursor: 'pointer',
                border: `1.5px solid ${priorityColor}50`,
                transition: 'transform 0.15s ease',
                '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 4px 12px ${ACCENT_GLOW}` },
                '&:active': { transform: 'scale(0.98)' },
                '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
            }}
        >
            <Stack spacing={0.5}>
                {time && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <CalendarMonthIcon sx={{ fontSize: 12, color: isOverdue ? '#E57373' : 'rgba(255,255,255,0.35)' }} />
                        <Typography sx={{ fontSize: '0.72rem', color: isOverdue ? '#E57373' : 'rgba(255,255,255,0.45)' }}>{time}</Typography>
                    </Stack>
                )}
                <Stack direction="row" spacing={0.5} alignItems="center">
                    <TypeIcon sx={{ fontSize: 14, color: typeColor }} />
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'white', lineHeight: 1.3 }} noWrap>
                        {task.title ?? '--'}
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
};

const DetailedInfoTask = () => {
    const { t } = useTranslation();
    const { state } = useLocation();
    const navigate = useNavigate();

    if (!state?.task) {
        navigate('/app/tasks', { replace: true });
        return null;
    }

    const [loading, setLoading] = useState(true);
    const [task, setTask] = useState(state.task);
    const [open, setOpen] = useState(false);
    const [taskToUpdate, setTaskToUpdate] = useState({});
    const [error, setError] = useState(false);
    const [loadingDialog, setLoadingDialog] = useState(false);
    const [successMsg, setSuccessMsg] = useState(false);
    const [detailRescheduleOpen, setDetailRescheduleOpen] = useState(false);
    const [detailRescheduleDate, setDetailRescheduleDate] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [configuredProps, setConfiguredProps] = useState([]);
    const triggerRef = useRef(null);

    useEffect(() => {
        loadData();
        api.fetchProperties('task').then((props) => {
            setConfiguredProps(Array.isArray(props) ? props : []);
        }).catch(() => {});
    }, []);

    const loadData = () => {
        api.fetchTask(task.id).then((response) => {
            setTaskToUpdate({
                subject: response.title,
                hs_task_type: response.type,
                description: response.description,
                priority: response.priority,
                status: response.status,
                dueDate: response.dueDate
            });
            setTask(response);
            setLoading(false);
        }).catch((err) => {
            console.error("Error fetching task", err);
            setLoading(false);
        });
    };

    const handleClose = () => { setOpen(false); triggerRef.current?.focus(); };

    const handleSave = () => {
        const taskNewData = { ...taskToUpdate, id: task.id };
        setLoadingDialog(true);
        api.updateTask(task.id, taskNewData).then(() => {
            setOpen(false);
            setLoadingDialog(false);
            setSuccessMsg(true);
            loadData();
        }).catch((err) => {
            console.error("Error updating task", err);
            setError(true);
            setLoadingDialog(false);
        });
    };

    const handleMarkComplete = () => {
        setActionLoading(true);
        api.updateTask(task.id, { status: 'COMPLETED' }).then(() => {
            setActionLoading(false);
            navigate('/app/tasks', { state: { ok: true } });
        }).catch((err) => {
            console.error("Error completing task", err);
            setActionLoading(false);
        });
    };

    const handleDetailReschedule = () => {
        if (!detailRescheduleDate) return;
        setActionLoading(true);
        const isoDate = detailRescheduleDate.valueOf();
        api.updateTask(task.id, { dueDate: isoDate }).then(() => {
            setDetailRescheduleOpen(false);
            setDetailRescheduleDate(null);
            setActionLoading(false);
            setSuccessMsg(true);
            loadData();
        }).catch((err) => {
            console.error("Error rescheduling task", err);
            setActionLoading(false);
        });
    };

    const priorityColor = getPriorityColor(task.priority);
    const statusColor = getStatusColor(task.status);
    const isOverdue = task.dueDate && dayjs(task.dueDate.split('Z')[0]).isBefore(dayjs(), 'day');
    const formattedDate = task.dueDate
        ? dayjs(task.dueDate.split('Z')[0]).tz(dayjs.tz.guess()).format('DD/MM/YYYY HH:mm')
        : null;
    const contact = task.contacts?.length > 0 ? task.contacts[0] : null;
    const { Icon: TypeIcon, color: typeColor } = getTypeConfig(task.type);

    return (
        <Stack sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto', width: '100%' }}>
            <Loading loading={loading} />

            {/* Header */}
            <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2.5 }}>
                <IconButton aria-label={t('goBack')} onClick={() => navigate('/app/tasks')} sx={{ color: 'white', mt: 0.25 }}>
                    <ArrowBack />
                </IconButton>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 }}>
                        {task.title ?? '--'}
                    </Typography>
                </Stack>
            </Stack>

            {/* Status (large) + Priority + Type badges */}
            <Stack direction="row" spacing={1} sx={{ mb: 2.5, flexWrap: 'wrap', gap: 0.75 }}>
                {task.status && (
                    <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 18, color: `${statusColor} !important` }} />}
                        label={t(task.status === 'NOT_STARTED' ? 'notStarted' : 'inProgress')}
                        sx={{
                            height: 36, borderRadius: '18px', fontSize: '0.82rem', fontWeight: 600,
                            backgroundColor: `${statusColor}15`, color: statusColor,
                            border: `1px solid ${statusColor}30`,
                        }}
                    />
                )}
                {task.priority && (
                    <Chip
                        icon={<PriorityHighIcon sx={{ fontSize: 14, color: `${priorityColor} !important` }} />}
                        label={t(task.priority.toLowerCase())}
                        size="small"
                        sx={{
                            height: 28, borderRadius: '14px', fontSize: '0.72rem', fontWeight: 600,
                            backgroundColor: `${priorityColor}15`, color: priorityColor,
                            border: `1px solid ${priorityColor}30`,
                        }}
                    />
                )}
                {task.type && (
                    <Chip
                        icon={<TypeIcon sx={{ fontSize: 13, color: `${typeColor} !important` }} />}
                        label={t(task.type.toLowerCase())}
                        size="small"
                        sx={{
                            height: 28, borderRadius: '14px', fontSize: '0.72rem',
                            backgroundColor: `${typeColor}12`, color: `${typeColor}CC`,
                            border: `1px solid ${typeColor}25`,
                        }}
                    />
                )}
            </Stack>

            {/* Overdue badge */}
            {isOverdue && (
                <Box sx={{
                    ...glassCard, mb: 2,
                    backgroundColor: 'rgba(229,115,115,0.08)', border: '1px solid rgba(229,115,115,0.2)',
                }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography sx={{ fontSize: '0.8rem', color: '#E57373', fontWeight: 600 }}>
                            {t('overdueTask')}
                        </Typography>
                        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(229,115,115,0.7)' }}>
                            {dayjs(task.dueDate.split('Z')[0]).fromNow()}
                        </Typography>
                    </Stack>
                </Box>
            )}

            {/* Due date hero card */}
            {formattedDate && (
                <Box sx={{
                    ...glassCard, mb: 2,
                    background: `linear-gradient(135deg, ${ACCENT_DARK}18, rgba(255,255,255,0.03))`,
                    border: `1px solid ${ACCENT}20`,
                }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                            width: 44, height: 44, borderRadius: '12px',
                            backgroundColor: `${ACCENT}15`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <CalendarMonthIcon sx={{ color: ACCENT, fontSize: 22 }} />
                        </Box>
                        <Stack spacing={0.25}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('dueDate')}
                            </Typography>
                            <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
                                {formattedDate}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            )}

            {/* Description card */}
            {task.description && (
                <Box sx={{ ...glassCard, mb: 2 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
                        <DescriptionIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('description')}
                        </Typography>
                    </Stack>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.88rem', lineHeight: 1.6, maxHeight: 200, overflow: 'auto' }}>
                        {task.description}
                    </Typography>
                </Box>
            )}

            {/* Reminders card */}
            {task.reminders && (
                <Box sx={{ ...glassCard, mb: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '10px',
                            backgroundColor: 'rgba(255,255,255,0.04)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                            <NotificationsIcon sx={{ color: '#FFB74D', fontSize: 20 }} />
                        </Box>
                        <Stack spacing={0.25}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                {t('reminders')}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.88rem' }}>
                                {task.reminders}
                            </Typography>
                        </Stack>
                    </Stack>
                </Box>
            )}

            {/* Contact card (tappable) */}
            {contact && (
                <Box
                    onClick={() => navigate('/app/contacts/detailed', { state: { contact } })}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/app/contacts/detailed', { state: { contact } }); } }}
                    aria-label={getContactName(contact)}
                    sx={{
                        ...glassCard, mb: 2, cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                        '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
                    }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                        <PersonIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.35)' }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('contact')}
                        </Typography>
                    </Stack>
                    <Stack spacing={1.25}>
                        <Typography sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem' }}>
                            {getContactName(contact)}
                        </Typography>
                        {contact.email && (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <MailOutlined sx={{ fontSize: 14, color: ACCENT, flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)' }}>{contact.email}</Typography>
                            </Stack>
                        )}
                        {contact.phone && (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <PhoneOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>{contact.phone}</Typography>
                            </Stack>
                        )}
                        {contact.company?.name && (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <WorkOutlineOutlined sx={{ fontSize: 14, color: 'rgba(255,255,255,0.35)', flexShrink: 0 }} />
                                <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.45)' }}>{contact.company.name}</Typography>
                            </Stack>
                        )}
                    </Stack>
                </Box>
            )}

            {/* Configured Properties */}
            {configuredProps.length > 0 && task.configuredProperties && (
                <Box data-testid="configured-properties-card" sx={{ ...glassCard, mb: 2 }}>
                    <Stack spacing={2}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            {t('additionalDetails')}
                        </Typography>
                        {configuredProps
                            .filter(prop => task.configuredProperties?.[prop.name] != null)
                            .map((prop, idx, arr) => (
                                <React.Fragment key={prop.name}>
                                    {idx > 0 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />}
                                    <Stack>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.72rem', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {prop.label}
                                        </Typography>
                                        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.88rem', fontWeight: 600 }}>
                                            {formatPropertyValue(prop, task.configuredProperties[prop.name])}
                                        </Typography>
                                    </Stack>
                                </React.Fragment>
                            ))
                        }
                    </Stack>
                </Box>
            )}

            {/* Complete button - hero CTA */}
            <Button
                fullWidth
                startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <CheckCircleIcon />}
                onClick={handleMarkComplete}
                disabled={actionLoading || task.status === 'COMPLETED'}
                data-testid="complete-task"
                sx={{
                    borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '0.9rem',
                    py: 1.5, mb: 1.5,
                    color: '#0a1929', backgroundColor: '#66BB6A',
                    '&:hover': { backgroundColor: '#81C784' },
                    '&:disabled': { backgroundColor: 'rgba(102,187,106,0.2)', color: 'rgba(255,255,255,0.3)' },
                }}
            >
                {t('markComplete')}
            </Button>

            {/* Reschedule - secondary */}
            <Button
                fullWidth
                variant="outlined"
                startIcon={<EventRepeatIcon />}
                onClick={() => setDetailRescheduleOpen(true)}
                disabled={actionLoading}
                data-testid="reschedule-task"
                sx={{
                    borderRadius: '14px', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                    py: 1.25, mb: 1.5,
                    color: '#42A5F5', borderColor: 'rgba(66,165,245,0.3)',
                    '&:hover': { borderColor: '#42A5F5', backgroundColor: 'rgba(66,165,245,0.08)' },
                    '&:disabled': { borderColor: 'rgba(66,165,245,0.1)', color: 'rgba(255,255,255,0.3)' },
                }}
            >
                {t('reschedule')}
            </Button>

            {/* Update task - tertiary text button */}
            <Button
                fullWidth
                ref={triggerRef}
                startIcon={<DriveFileMoveOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={() => setOpen(true)}
                data-testid="update-task"
                sx={{
                    borderRadius: '14px', textTransform: 'none', fontWeight: 600, fontSize: '0.85rem',
                    py: 1, mb: 2,
                    color: ACCENT,
                    '&:hover': { backgroundColor: `${ACCENT}10` },
                    '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 },
                }}
            >
                {t('updateTask')}
            </Button>

            {/* Update dialog */}
            <Dialog open={open} onClose={handleClose} aria-labelledby="update-task-title" aria-describedby="update-task-description"
                PaperProps={{ sx: { backgroundColor: '#0f1b2e', backgroundImage: 'none', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', maxWidth: 480, width: '100%', mx: 2 } }}
            >
                <DialogTitle id="update-task-title" sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', pb: 0.5 }}>
                    {t('updateTask')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="update-task-description" sx={{ mb: 1.5, color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                        {t('updateTaskDescription')}
                    </DialogContentText>
                    <Stack direction="column" alignItems="center" spacing={1}>
                        {error && <Alert severity="error" sx={{ borderRadius: '12px', width: '100%' }}>{t('errorUpdatingTask')}</Alert>}
                        {loadingDialog && <CircularProgress sx={{ color: ACCENT }} />}
                        {!loadingDialog && <Task task={taskToUpdate} setTask={setTaskToUpdate} loaded={() => {}} setNextDisabled={() => {}} />}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={handleClose}
                        sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, borderRadius: '12px', px: 2.5, '&:hover': { backgroundColor: 'rgba(255,255,255,0.05)' } }}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleSave}
                        sx={{ color: '#0a1929', fontWeight: 600, backgroundColor: ACCENT, borderRadius: '12px', px: 2.5, '&:hover': { backgroundColor: '#8bbfc2' } }}>
                        {t('save')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Detail reschedule dialog */}
            <Dialog open={detailRescheduleOpen} onClose={() => setDetailRescheduleOpen(false)}
                PaperProps={{ sx: { backgroundColor: '#0f1b2e', backgroundImage: 'none', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.08)', maxWidth: 400, width: '100%', mx: 2 } }}
            >
                <DialogTitle sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', pb: 0.5 }}>
                    {t('reschedule')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2, color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem' }}>
                        {t('rescheduleTaskDescription')}
                    </DialogContentText>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <MobileDateTimePicker
                            label={t('newDate')}
                            value={detailRescheduleDate}
                            onChange={setDetailRescheduleDate}
                        />
                    </LocalizationProvider>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button onClick={() => { setDetailRescheduleOpen(false); setDetailRescheduleDate(null); }}
                        sx={{ color: 'rgba(255,255,255,0.6)', fontWeight: 600, borderRadius: '12px', px: 2.5 }}>
                        {t('cancel')}
                    </Button>
                    <Button onClick={handleDetailReschedule} disabled={!detailRescheduleDate || actionLoading}
                        sx={{
                            color: '#0a1929', fontWeight: 600, backgroundColor: '#42A5F5', borderRadius: '12px', px: 2.5,
                            '&:hover': { backgroundColor: '#64B5F6' },
                            '&:disabled': { backgroundColor: 'rgba(66,165,245,0.3)', color: 'rgba(255,255,255,0.3)' },
                        }}>
                        {actionLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('reschedule')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={successMsg} autoHideDuration={3000} onClose={() => setSuccessMsg(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="success" onClose={() => setSuccessMsg(false)} sx={{ borderRadius: '12px' }}>
                    {t('taskUpdated')}
                </Alert>
            </Snackbar>
        </Stack>
    );
};

const formatPropertyValue = (prop, value) => {
    if (value === null || value === undefined || value === '') return '—';
    if (prop.type === 'date') return dayjs(value).format('L');
    if (prop.type === 'datetime') return dayjs(value).format('L LT');
    if (prop.type === 'enumeration' && prop.options?.length > 0) {
        const values = String(value).split(';');
        return values.map(v => prop.options.find(o => o.value === v)?.label || v).join(', ');
    }
    if (prop.type === 'number') return Number(value).toLocaleString();
    return String(value);
};

export default {
    Tasks,
    TaskCard,
    DetailedInfoTask
};
